import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api";
import urls from "../urls";
import type { MeteringOrMarketLocation } from "../utils/backend-types";
import { COMPONENT_QUERY_KEY } from "../utils/constants";
import type { ObjectName } from "../utils/enums";
import {
  mergeMeteringOrMarketLocationIntoListCache,
  meteringOrMarketLocationQueries,
  updateMeteringOrMarketLocationInListCache
} from "./useMeteringOrMarketLocation";
import { useOnSiteStructureChanged } from "./useOnSiteStructureChanged";

export function useMeteringOrMarketLocationMutation(
  objectName: ObjectName.MeteringLocation | ObjectName.MarketLocation,
  mode: "create" | "edit",
  siteId: number
) {
  const queryClient = useQueryClient();
  const onSiteStructureChanged = useOnSiteStructureChanged(siteId);
  const cacheName = COMPONENT_QUERY_KEY[objectName];

  return useMutation({
    mutationFn: (newData: MeteringOrMarketLocation) =>
      mutateMeteringOrMarketLocation(newData, objectName, mode, siteId),
    mutationKey: [cacheName, { siteOrVariantId: siteId }],
    onSuccess(responseData) {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });

      if (mode === "create") {
        mergeMeteringOrMarketLocationIntoListCache(
          queryClient,
          { siteId },
          responseData.data
        );
      } else {
        updateMeteringOrMarketLocationInListCache(
          queryClient,
          { siteId },
          responseData.data
        );
        queryClient.invalidateQueries({
          queryKey: meteringOrMarketLocationQueries.detail(
            objectName,
            responseData.data.id
          ).queryKey
        });
      }

      queryClient.invalidateQueries({
        queryKey: meteringOrMarketLocationQueries.all()
      });

      onSiteStructureChanged();
    }
  });
}

function mutateMeteringOrMarketLocation(
  newData: MeteringOrMarketLocation,
  objectName: ObjectName.MeteringLocation | ObjectName.MarketLocation,
  mode: "create" | "edit",
  siteId: number
) {
  if (mode === "create") {
    return createMeteringOrMarketLocation(newData, siteId);
  }

  return editMeteringOrMarketLocation(newData);
}

function createMeteringOrMarketLocation(
  newLocation: MeteringOrMarketLocation,
  siteId: number
) {
  const url = urls.api.meteringOrMarketLocations(siteId);
  return api.post<MeteringOrMarketLocation>(url, {
    ...newLocation,
    site: siteId
  });
}

function editMeteringOrMarketLocation(
  editedLocation: MeteringOrMarketLocation
) {
  const url = urls.api.meteringOrMarketLocation(editedLocation.id);
  return api.patch<MeteringOrMarketLocation>(url, editedLocation);
}
